import { Controller } from "@hotwired/stimulus"

// Manages dynamic beneficiary forms for funerary plans
export default class extends Controller {
  static targets = [
    "beneficiariosContainer", "beneficiariosCount", "beneficiariosEmpty",
    "agregadosContainer", "agregadosCount", "agregadosEmpty",
    "addBeneficiario", "addAgregado",
    "beneficiarioTemplate", "agregadoTemplate",
    "maxBeneficiariosDisplay", "maxAgregadosDisplay"
  ]
  static values = {
    maxBeneficiaries: { type: Number, default: 10 },
    maxAgregados: { type: Number, default: 5 },
    index: { type: Number, default: 0 },
    customerName: { type: String, default: "" },
    customerCpf: { type: String, default: "" },
    customerBirthdate: { type: String, default: "" }
  }

  connect() {
    this.updateCounts()
    this.updateButtonStates()
  }

  maxBeneficiariesValueChanged() {
    this.updateButtonStates()
    if (this.hasMaxBeneficiariosDisplayTarget) {
      this.maxBeneficiariosDisplayTarget.textContent = this.maxBeneficiariesValue
    }
  }

  maxAgregadosValueChanged() {
    this.updateButtonStates()
    if (this.hasMaxAgregadosDisplayTarget) {
      this.maxAgregadosDisplayTarget.textContent = this.maxAgregadosValue
    }
  }

  toggleSection(event) {
    // Don't toggle if clicking on add button
    if (event.target.closest("button")) return

    const header = event.currentTarget
    const section = header.closest("[data-section]")
    if (!section) return

    const content = section.querySelector(".accordion-content")
    const icon = section.querySelector(".accordion-icon")

    if (content && icon) {
      content.classList.toggle("hidden")
      icon.classList.toggle("rotate-180")
    }
  }

  addBeneficiario(event) {
    event.preventDefault()
    this.addBeneficiary("beneficiario")
  }

  addAgregado(event) {
    event.preventDefault()
    this.addBeneficiary("agregado")
  }

  addBeneficiary(type) {
    const counts = this.getCounts()

    // Check limits
    if (type === "agregado" && counts.agregados >= this.maxAgregadosValue) {
      alert(`Limite máximo de agregados atingido (${this.maxAgregadosValue})`)
      return
    }

    if (type === "beneficiario" && counts.beneficiarios >= this.maxBeneficiariesValue) {
      alert(`Limite máximo de beneficiários atingido (${this.maxBeneficiariesValue})`)
      return
    }

    // Get the correct template
    const template = type === "beneficiario"
      ? this.beneficiarioTemplateTarget
      : this.agregadoTemplateTarget

    const index = this.indexValue
    this.indexValue = index + 1

    const newContent = template.innerHTML.replace(/NEW_INDEX/g, index)

    const wrapper = document.createElement("div")
    wrapper.innerHTML = newContent

    // Get the correct container
    const container = type === "beneficiario"
      ? this.beneficiariosContainerTarget
      : this.agregadosContainerTarget

    // Hide empty message (check if target exists first)
    if (type === "beneficiario" && this.hasBeneficiariosEmptyTarget) {
      this.beneficiariosEmptyTarget.classList.add("hidden")
    } else if (type === "agregado" && this.hasAgregadosEmptyTarget) {
      this.agregadosEmptyTarget.classList.add("hidden")
    }

    // Expand section if collapsed
    if (container.classList.contains("hidden")) {
      container.classList.remove("hidden")
      const section = container.closest("[data-section]")
      const icon = section?.querySelector(".accordion-icon")
      if (icon) {
        icon.classList.add("rotate-180")
      }
    }

    container.appendChild(wrapper.firstElementChild)

    this.updateCounts()
    this.updateButtonStates()

    // Focus on the name field
    const nameField = container.lastElementChild.querySelector("input[name*='name']")
    if (nameField) {
      nameField.focus()
    }
  }

  remove(event) {
    event.preventDefault()
    const item = event.target.closest("[data-beneficiary-item]")
    if (!item) return

    // Don't allow removal of titular
    const type = item.dataset.beneficiaryType
    if (type === "titular") return

    // If persisted, mark for destruction; otherwise remove from DOM
    const destroyField = item.querySelector("input[name*='_destroy']")
    if (destroyField) {
      destroyField.value = "1"
      item.classList.add("hidden")
    } else {
      item.remove()
    }

    this.updateCounts()
    this.updateButtonStates()
    this.showEmptyMessageIfNeeded()
  }

  getCounts() {
    let beneficiarios = 0
    let agregados = 0

    // Count beneficiários
    if (this.hasBeneficiariosContainerTarget) {
      const beneficiarioItems = this.beneficiariosContainerTarget.querySelectorAll("[data-beneficiary-item]:not(.hidden)")
      beneficiarios = beneficiarioItems.length
    }

    // Count agregados
    if (this.hasAgregadosContainerTarget) {
      const agregadoItems = this.agregadosContainerTarget.querySelectorAll("[data-beneficiary-item]:not(.hidden)")
      agregados = agregadoItems.length
    }

    return { beneficiarios, agregados }
  }

  updateCounts() {
    const counts = this.getCounts()

    // Update section counts (just the number)
    if (this.hasBeneficiariosCountTarget) {
      this.beneficiariosCountTarget.textContent = counts.beneficiarios
    }

    if (this.hasAgregadosCountTarget) {
      this.agregadosCountTarget.textContent = counts.agregados
    }
  }

  updateButtonStates() {
    const counts = this.getCounts()

    // Beneficiário button
    if (this.hasAddBeneficiarioTarget) {
      const disabled = counts.beneficiarios >= this.maxBeneficiariesValue
      this.addBeneficiarioTarget.disabled = disabled
      this.addBeneficiarioTarget.classList.toggle("opacity-50", disabled)
      this.addBeneficiarioTarget.classList.toggle("cursor-not-allowed", disabled)
    }

    // Agregado button
    if (this.hasAddAgregadoTarget) {
      const disabled = counts.agregados >= this.maxAgregadosValue
      this.addAgregadoTarget.disabled = disabled
      this.addAgregadoTarget.classList.toggle("opacity-50", disabled)
      this.addAgregadoTarget.classList.toggle("cursor-not-allowed", disabled)
    }
  }

  showEmptyMessageIfNeeded() {
    const counts = this.getCounts()

    if (this.hasBeneficiariosEmptyTarget && counts.beneficiarios === 0) {
      this.beneficiariosEmptyTarget.classList.remove("hidden")
    }

    if (this.hasAgregadosEmptyTarget && counts.agregados === 0) {
      this.agregadosEmptyTarget.classList.remove("hidden")
    }
  }

  updateDisplayName(event) {
    const item = event.target.closest("[data-beneficiary-item]")
    if (!item) return

    const displayName = item.querySelector(".beneficiary-display-name")
    const type = item.dataset.beneficiaryType

    if (displayName) {
      const name = event.target.value.trim()
      if (name) {
        displayName.textContent = name
      } else {
        displayName.textContent = `Novo ${this.getTypeLabel(type)}`
      }
    }
  }

  getTypeLabel(type) {
    switch (type) {
      case "titular": return "Titular"
      case "beneficiario": return "Beneficiário"
      case "agregado": return "Agregado"
      default: return type
    }
  }
}
